import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Trophy, Users, Calendar, Target, Heart, Star } from 'lucide-react';
import { eventosAPI, conquistasAPI, configAPI } from '@/lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5aeb9d65-c68c-4f8e-b371-f49441118154/artifacts/y8ms66px_06.png";
const HERO_BG = "https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/pcuf1s0w_WhatsApp%20Image%202026-03-05%20at%2008.26.28.jpeg";

export default function Home() {
  const [eventos, setEventos] = useState([]);
  const [conquistas, setConquistas] = useState([]);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventosRes, conquistasRes, configRes] = await Promise.all([
          eventosAPI.listar(),
          conquistasAPI.listar(),
          configAPI.obter()
        ]);
        setEventos(eventosRes.data.slice(0, 3));
        setConquistas(conquistasRes.data.slice(0, 4));
        setConfig(configRes.data);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { icon: Users, value: '50+', label: 'Atletas' },
    { icon: Trophy, value: '10+', label: 'Títulos' },
    { icon: Calendar, value: '4+', label: 'Anos' },
    { icon: Heart, value: '100%', label: 'Dedicação' },
  ];

  const values = [
    { icon: Heart, title: 'Paixão', desc: 'Amor pelo voleibol em cada jogada' },
    { icon: Target, title: 'Disciplina', desc: 'Compromisso com a excelência' },
    { icon: Users, title: 'Respeito', desc: 'União e trabalho em equipe' },
    { icon: Star, title: 'Superação', desc: 'Sempre buscando evoluir' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Voleibol"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient hero-pattern" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img src={LOGO_URL} alt="ACEC Logo" className="h-32 sm:h-40 mx-auto mb-8" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight mb-6">
              ACEC <span className="text-yellow-400">Feminino</span>
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 max-w-2xl mx-auto mb-10 font-light">
              Transformando vidas através do voleibol em Canaã dos Carajás
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/sobre"
                data-testid="hero-cta-sobre"
                className="btn-cta inline-flex items-center justify-center gap-2"
              >
                Conheça Nossa História
                <ChevronRight size={20} />
              </Link>
              <Link
                to="/apoie"
                data-testid="hero-cta-apoie"
                className="btn-secondary border-white text-white hover:bg-white/10 inline-flex items-center justify-center gap-2"
              >
                Seja um Apoiador
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-700" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-10 h-10 mx-auto mb-3 text-yellow-400" />
                <div className="font-accent text-5xl sm:text-6xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-blue-200 uppercase tracking-wider text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="text-yellow-500 font-bold uppercase tracking-widest text-sm">
                Sobre Nós
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 uppercase">
                Mais que um Time,<br />
                <span className="text-blue-700">Uma Família</span>
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                {config?.sobre_texto || 
                  "Somos atletas apaixonados pelo vôlei, com anos de experiência dentro e fora das quadras. Sabemos, na prática, como esse esporte é capaz de transformar vidas — não apenas no aspecto físico, mas também no social, emocional e pessoal."}
              </p>
              <Link
                to="/sobre"
                data-testid="about-preview-link"
                className="inline-flex items-center gap-2 text-blue-700 font-bold uppercase tracking-wider hover:text-blue-800 transition-colors"
              >
                Saiba Mais <ChevronRight size={20} />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_c5c7b024-2dc8-4b4b-80a1-e26f6491db2a/artifacts/v1dcyyt3_WhatsApp%20Image%202026-03-05%20at%2008.49.11.jpeg"
                  alt="Time ACEC"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 rounded-xl p-6 shadow-xl">
                <div className="font-accent text-5xl font-bold text-blue-900">2021</div>
                <div className="text-blue-900 font-medium">Fundação</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 section-gradient-blue" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-yellow-500 font-bold uppercase tracking-widest text-sm">
              Nossos Valores
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 uppercase mt-2">
              O Que Nos <span className="text-blue-700">Move</span>
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 text-center card-shadow card-shadow-hover transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-blue-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-6">
              Faça Parte Desta <span className="text-yellow-400">História</span>
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Sua contribuição pode transformar a vida de uma atleta. Apoie o projeto ACEC Feminino e ajude a formar as campeãs do amanhã.
            </p>
            <Link
              to="/apoie"
              data-testid="cta-apoie-final"
              className="btn-cta inline-flex items-center gap-2"
            >
              Quero Apoiar
              <Heart className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
